import history from '../history'
import {
  getFiles,
  getFile,
  downloadFile
} from '../library/aws'

import {
  paginateItems,
  sortItems
} from '../library/general'

import {
  UPDATE_USER_ATTRIBUTES
} from './../stores/auth'

export const CLIENT_NAME_COLUMN = 'clientName'
export const PROGRAM_NAME_COLUMN = 'programName'
export const FILE_NAME_COLUMN = 'fileName'
export const AIRING_STATIONS_COLUMN = 'airingStations'
export const DELIVERY_TO_COLUMN = 'deliverTo'
export const DELIVERY_DATE_COLUMN = 'deliveryDateTime'

export const CLIENT_NAME_COLUMN_FRIENDLY = 'Client'
export const PROGRAM_NAME_COLUMN_FRIENDLY = 'Program'
export const FILE_NAME_COLUMN_FRIENDLY = 'File'
export const AIRING_STATIONS_COLUMN_FRIENDLY = 'Airing Station(s)'
export const DELIVERY_TO_COLUMN_FRIENDLY = 'Delivery To'
export const DELIVERY_DATE_COLUMN_FRIENDLY = 'Delivery Date'

const initialState = {
  files: [],
  subsetFiles: [],
  loading: false,
  error: '',
  hasError: false,
  pageNumber: 1,
  pageSize: 20,
  totalSubsetFiles: 0,
  numberOfPages: 0,
  // default sort direction
  sortColumn: DELIVERY_DATE_COLUMN,
  sortDirection: {
    [DELIVERY_DATE_COLUMN]: 'desc'
  },
  searchTerm: '',
  // Dropdown filters ('' = all), applied together with the search term (see requestDropdownFilters)
  clientFilter: '',
  programFilter: '',
  filesById: {},
  linkToDeliverable: {
    loading: false,
    show: false,
    hasError: false,
    error: ''
  }
}

const RESET_STATE = 'files/RESET_STATE'
const UPDATE = 'files/UPDATE'
const UPDATE_SORT = 'files/UPDATE_SORT'
const UPDATE_FILE = 'files/UPDATE_FILE'
const UPDATE_LINK_TO_DELIVERABLE = 'files/UPDATE_LINK_TO_DELIVERABLE'
const UPDATE_SOURCE_LINK_ON_FILE = 'files/UPDATE_SOURCE_LINK_ON_FILE'

const handler = (state = initialState, action) => {
  const {payload} = action
  const {filesById} = state

  switch (action.type) {
    case RESET_STATE:
      return {
        ...initialState,
      }

    case UPDATE:
      return {
        ...state,
        ...payload
      }

    case UPDATE_SORT:
      return {
        ...state,
        sortColumn: payload.sortColumn,
        sortDirection: {
          ...payload.sortDirection
        }
      }

    case UPDATE_FILE:
      filesById[payload.fileId] = payload

      return {
        ...state,
        filesById: {
          ...filesById
        }
      }

    case UPDATE_SOURCE_LINK_ON_FILE:
      return {
        ...state,
        filesById: {
          ...filesById,
          [payload.fileId]: {
            ...filesById[payload.fileId],
            sourceLink: payload.sourceLink
          }
        }
      }

    case UPDATE_LINK_TO_DELIVERABLE:
      return {
        ...state,
        linkToDeliverable: {
          ...state.linkToDeliverable,
          ...payload
        }
      }

    default:
      return state
  }
}

export const requestFiles = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: RESET_STATE
    })

    dispatch({
      type: UPDATE,
      payload: {
        loading: true,
        hasError: false,
        error: '',
        searchTerm: '',
      }
    })

    try {
      const {
        sortColumn,
        sortDirection,
        pageSize,
        pageNumber,
        searchTerm,
      clientFilter,
      programFilter
      } = getState().stores.files

      const {
        user
      } = getState().stores.auth

      const data = await getFiles(user)
      const {
        files,
        contactFullName,
        contactEmailAddress,
        accountName
      } = data

      buildSearchIndex(files)

      dispatch({
        type: UPDATE_USER_ATTRIBUTES,
        payload: {
          contactName: contactFullName,
          contactEmail: contactEmailAddress,
          accountName
        }
      })

      const subsetFilesBeforePagination = sortFiles(filterFiles(files, searchTerm, clientFilter, programFilter), sortColumn, sortDirection[sortColumn])

      const totalSubsetFiles = subsetFilesBeforePagination.length
      const numberOfPages = Math.ceil(totalSubsetFiles / pageSize)

      subsetFilesBeforePagination.forEach((f, i) => f['#'] = i + 1)

      const subsetFiles = paginateFiles(subsetFilesBeforePagination, pageNumber, pageSize)

      subsetFiles.forEach((f, i) => f['#'] = i + 1)

      dispatch({
        type: UPDATE,
        payload: {
          files,
          subsetFiles,
          numberOfPages,
          totalSubsetFiles
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE,
        payload: {
          error: e.message,
          hasError: true
        }
      })
    }

    dispatch({
      type: UPDATE,
      payload: {
        loading: false
      }
    })
  }
}

// Same pipeline as the Client Portal orders store:
//   1. search filtering  2. sorting (shared, date-aware sortItems)  3. pagination (shared paginateItems)
// For large file lists the searchable text of every row is lowercased ONCE when the files are fetched
// (buildSearchIndex), so each keystroke is a single substring check per row instead of one per column.

const SEARCHABLE_COLUMNS = [
  CLIENT_NAME_COLUMN,
  PROGRAM_NAME_COLUMN,
  FILE_NAME_COLUMN,
  AIRING_STATIONS_COLUMN,
  DELIVERY_TO_COLUMN
]

const formatDeliveryDate = value => {
  if (!value) return ''
  const d = new Date(value)
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`
}

export const buildSearchIndex = files =>
  files.forEach(f => {
    f.searchIndex = SEARCHABLE_COLUMNS
      .map(c => f[c] || '')
      .concat(formatDeliveryDate(f[DELIVERY_DATE_COLUMN]))
      .join(' ')
      .toLowerCase()
  })

const filterFiles = (files, searchTerm = '', clientFilter = '', programFilter = '') => {
  let result = files

  if (clientFilter) result = result.filter(f => (f[CLIENT_NAME_COLUMN] || '') === clientFilter)
  if (programFilter) result = result.filter(f => (f[PROGRAM_NAME_COLUMN] || '') === programFilter)

  const term = searchTerm.trim().toLowerCase()

  if (!term) return result

  // Every whitespace-separated word must appear somewhere in the row
  const words = term.split(/\s+/)

  return result.filter(f => {
    const haystack = f.searchIndex !== undefined ? f.searchIndex : ''
    return words.every(w => haystack.includes(w))
  })
}

// Distinct, sorted values for the dropdown filters (programs are narrowed to the selected client in the view)
export const distinctValues = (files, column, predicate = () => true) =>
  [...new Set(files.filter(predicate).map(f => f[column]).filter(Boolean))].sort((a, b) => a.localeCompare(b))

const sortFiles = (files, sortColumn, sortDirection) =>
  sortItems(files, sortColumn, sortDirection)

const paginateFiles = (files, pageNumber, pageSize) =>
  paginateItems(files, pageNumber, pageSize)

export const requestSortFiles = (sortColumn, sortDirection) => {
  return async(dispatch, getState) => {
    const {
      files,
      pageNumber,
      pageSize,
      searchTerm,
    clientFilter,
    programFilter
    } = getState().stores.files

    // Search Filtering
    // sorting
    // pagination
    // Return subsetFiles

    const subsetFilesBeforePagination = sortFiles(filterFiles(files, searchTerm, clientFilter, programFilter), sortColumn, sortDirection)

    const totalSubsetFiles = subsetFilesBeforePagination.length
    const numberOfPages = Math.ceil(totalSubsetFiles / pageSize)

    subsetFilesBeforePagination.forEach((f, i) => f['#'] = i + 1)

    const subsetFiles = paginateFiles(subsetFilesBeforePagination, pageNumber, pageSize)
    subsetFiles.forEach((f, i) => f['#'] = i + 1)

    dispatch({
      type: UPDATE_SORT,
      payload: {
        sortColumn,
        sortDirection: {
          [sortColumn]: sortDirection
        }
      }
    })

    dispatch({
      type: UPDATE,
      payload: {
        subsetFiles,
        numberOfPages,
        totalSubsetFiles,
        pageNumber: 1
      }
    })
  }
}

export const requestSearch = searchTerm => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        loading: true,
        searchTerm,
        pageNumber: 1
      }
    })

    const {
      files,
      sortColumn,
      sortDirection,
      pageSize,
      pageNumber,
      clientFilter,
      programFilter
    } = getState().stores.files

    const subsetFilesBeforePagination = sortFiles(filterFiles(files, searchTerm, clientFilter, programFilter), sortColumn, sortDirection[sortColumn])

    const totalSubsetFiles = subsetFilesBeforePagination.length
    const numberOfPages = Math.ceil(totalSubsetFiles / pageSize)

    subsetFilesBeforePagination.forEach((f, i) => f['#'] = i + 1)

    const subsetFiles = paginateFiles(subsetFilesBeforePagination, pageNumber, pageSize)
    subsetFiles.forEach((f, i) => f['#'] = i + 1)

    dispatch({
      type: UPDATE,
      payload: {
        loading: false,
        subsetFiles,
        numberOfPages,
        totalSubsetFiles
      }
    })
  }
}

// Client / Program dropdown filters; '' clears one. Resets to page 1 and recomputes the visible subset.
export const requestDropdownFilters = ({clientFilter, programFilter}) => {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        clientFilter: clientFilter || '',
        programFilter: programFilter || '',
        pageNumber: 1
      }
    })

    const {
      files,
      sortColumn,
      sortDirection,
      pageSize,
      pageNumber,
      searchTerm
    } = getState().stores.files

    const subsetFilesBeforePagination = sortFiles(
      filterFiles(files, searchTerm, clientFilter || '', programFilter || ''),
      sortColumn,
      sortDirection[sortColumn]
    )

    const totalSubsetFiles = subsetFilesBeforePagination.length
    const numberOfPages = Math.ceil(totalSubsetFiles / pageSize)

    subsetFilesBeforePagination.forEach((f, i) => f['#'] = i + 1)

    const subsetFiles = paginateFiles(subsetFilesBeforePagination, pageNumber, pageSize)

    subsetFiles.forEach((f, i) => f['#'] = i + 1)

    dispatch({
      type: UPDATE,
      payload: {
        subsetFiles,
        numberOfPages,
        totalSubsetFiles
      }
    })
  }
}

export const requestLoadfile = fileId => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        hasError: false,
        error: ''
      }
    })

    history.push(`/view/${fileId}`)
  }
}

export const requestLoadAllFiles = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        hasError: false,
        error: ''
      }
    })

    history.push(`/view/`)
  }
}

export const requestPageStep = nextPageNumber => {
  return async(dispatch, getState) => {
    const {
      files,
      pageSize,
      numberOfPages,
      searchTerm,
      clientFilter,
      programFilter,
      sortColumn,
      sortDirection
    } = getState().stores.files

    if (nextPageNumber <= numberOfPages && nextPageNumber > 0) {
      dispatch({
        type: UPDATE,
        payload: {
          pageNumber: nextPageNumber
        }
      })

      const subsetFilesBeforePagination = sortFiles(filterFiles(files, searchTerm, clientFilter, programFilter), sortColumn, sortDirection[sortColumn])

      const totalSubsetFiles = subsetFilesBeforePagination.length
      const numberOfPages = Math.ceil(totalSubsetFiles / pageSize)

      subsetFilesBeforePagination.forEach((f, i) => f['#'] = i + 1)

      const subsetFiles = paginateFiles(subsetFilesBeforePagination, nextPageNumber, pageSize)

      dispatch({
        type: UPDATE,
        payload: {
          loading: false,
          subsetFiles,
          numberOfPages,
          totalSubsetFiles
        }
      })
    }
  }
}

export const requestFile = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE,
      payload: {
        loading: true,
        hasError: false,
        error: ''
      }
    })

    try {
      const {
        pathname
      } = history.location

      const sfidRegex = /\/view\/(.*)/ig
      const sfid = sfidRegex.exec(pathname)[1]

      const {
        user
      } = getState().stores.auth

      const data = await getFile(user, sfid)

      const {
        files,
        contactFullName,
        contactEmailAddress,
        accountName
      } = data

      dispatch({
        type: UPDATE_USER_ATTRIBUTES,
        payload: {
          contactName: contactFullName,
          contactEmail: contactEmailAddress,
          accountName
        }
      })

      dispatch({
        type: UPDATE_FILE,
        payload: files[0]
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE,
        payload: {
          error: e.message,
          hasError: true
        }
      })
    }

    dispatch({
      type: UPDATE,
      payload: {
        loading: false
      }
    })
  }
}

export const requestLinkToDeliverable = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_LINK_TO_DELIVERABLE,
      payload: {
        show: true,
        loading: true,
        hasError: false,
        error: ''
      }
    })

    try {
      const {
        pathname
      } = history.location

      const {
        filesById
      } = getState().stores.files

      const sfidRegex = /\/view\/(.*)/ig
      const sfid = sfidRegex.exec(pathname)[1]

      // Perform only when Source Link is not already available
      if (!filesById[sfid].sourceLink) {
        const {
          user
        } = getState().stores.auth

        const data = await downloadFile(user, sfid)

        dispatch({
          type: UPDATE_SOURCE_LINK_ON_FILE,
          payload: {
            fileId: sfid,
            sourceLink: data.files[0].sourceLink
          }
        })
      }
    }
    catch (e) {
      dispatch({
        type: UPDATE_LINK_TO_DELIVERABLE,
        payload: {
          error: e.message,
          hasError: true
        }
      })
    }

    dispatch({
      type: UPDATE_LINK_TO_DELIVERABLE,
      payload: {
        loading: false
      }
    })
  }
}

export const requestCloseLinkToDeliverable = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_LINK_TO_DELIVERABLE,
      payload: {
        show: false,
        loading: false,
        hasError: false,
        error: ''
      }
    })
  }
}

export default handler